
import { useState, useRef, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { getCreatorInitials } from "@/utils/dataUtils/formatters";
import { Separator } from "@/components/ui/separator";
import { useSocial } from "@/hooks/useSocial";
import { Comment } from "@/api/social";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  MoreVertical, 
  Edit, 
  Trash2, 
  Send, 
  Loader2,
  AlertCircle,
  MessageSquare,
  Star
} from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CommentsProps {
  derivativeId: string;
  maxHeight?: string;
}

export function Comments({ derivativeId, maxHeight = "400px" }: CommentsProps) {
  const { 
    comments, 
    isLoading, 
    isCommentLoading, 
    addComment, 
    updateComment, 
    deleteComment,
    fetchComments,
    commentsLoaded,
    canComment
  } = useSocial(derivativeId);
  const { user } = useAuth();
  
  const [newComment, setNewComment] = useState("");
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  // Force fetch comments when component mounts
  useEffect(() => {
    if (!commentsLoaded && !isRefreshing) {
      setIsRefreshing(true);
      fetchComments().finally(() => setIsRefreshing(false));
    }
  }, [commentsLoaded, fetchComments]);
  
  useEffect(() => {
    if (editingComment && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingComment]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const result = await addComment(newComment);
    if (result && (result.success || result.data)) {
      setNewComment("");
    }
  };

  const handleStartEdit = (comment: Comment) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditContent("");
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editContent.trim()) return;
    
    const success = await updateComment(commentId, editContent);
    if (success) {
      setEditingComment(null);
      setEditContent("");
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "d MMM yyyy à HH:mm", { locale: fr });
    } catch (e) {
      return typeof dateString === 'string' ? dateString : 'Date invalide';
    }
  };

  if (isLoading || isRefreshing) {
    return (
      <div className="p-4 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-16 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-grow" style={{ maxHeight }}>
        <div className="space-y-5 p-4">
          {!comments || comments.length === 0 ? (
            <div className="text-center py-12 flex flex-col items-center justify-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 mb-3 opacity-20" />
              <p className="font-medium">Aucun commentaire pour le moment.</p>
              {canComment && <p className="text-sm mt-1">Soyez le premier à commenter!</p>}
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="group space-y-2 rounded-lg hover:bg-muted/40 p-3 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10 border">
                      <AvatarImage 
                        src={comment.creator?.avatar || ''}
                        alt={(comment.creator?.name || 'Utilisateur') + ''} 
                      />
                      <AvatarFallback>
                        {getCreatorInitials(comment.creator?.name || "")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium">
                          {comment.creator?.name ? String(comment.creator.name) : 'Utilisateur'}
                        </span>
                        {comment.creator?.is_academy && (
                          <Star className="h-3.5 w-3.5 text-primary fill-primary" />
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDate(comment.created_at)}
                          {comment.is_edited && <span className="ml-1">(modifié)</span>}
                        </span>
                      </div>
                      
                      {editingComment === comment.id ? (
                        <div className="mt-2 space-y-2">
                          <Textarea
                            ref={editInputRef}
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={3}
                            placeholder="Modifiez votre commentaire..."
                            className="resize-none"
                          />
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={handleCancelEdit}
                            >
                              Annuler
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={() => handleSaveEdit(comment.id)}
                              disabled={isCommentLoading}
                            >
                              {isCommentLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                "Enregistrer"
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap text-sm leading-relaxed mt-1">
                          {typeof comment.content === 'string' ? comment.content : 
                           comment.content ? String(comment.content) : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  {user?.id === comment.user_id && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0">
                          <span className="sr-only">Menu</span>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuItem onClick={() => handleStartEdit(comment)} className="cursor-pointer">
                          <Edit className="mr-2 h-4 w-4" />
                          <span>Modifier</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => deleteComment(comment.id)}
                          className="text-destructive focus:text-destructive cursor-pointer"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Supprimer</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <Separator className="mt-3 opacity-50" />
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {canComment ? (
        <div className="pt-3 mt-auto px-4 pb-3 border-t bg-background/80 backdrop-blur-sm">
          <div className="flex gap-3">
            <Avatar className="h-10 w-10 border">
              <AvatarImage src={user?.user_metadata?.avatar_url || ''} />
              <AvatarFallback>
                {getCreatorInitials(user?.user_metadata?.full_name || "")}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <Textarea
                ref={commentInputRef}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ajouter un commentaire..."
                className="resize-none min-h-[80px] p-3"
                rows={3}
              />
              <div className="flex justify-end">
                <Button 
                  onClick={handleAddComment} 
                  size="default"
                  disabled={!newComment.trim() || isCommentLoading}
                  className="gap-2 px-5"
                >
                  {isCommentLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-1" />
                  )}
                  Commenter
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Alert className="m-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Seuls les membres de l'Academy peuvent ajouter des commentaires.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
